import { useEffect, useRef, useState } from 'react';
import { ArrowTopRightOnSquareIcon, ChevronDownIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useConfigurations, useCreateConfiguration } from '../../hooks/useSlaPricing';
import type { SlaPricingConfigurationSummary } from '../../services/api/slaPricing';
import type { LeadSlaPricingConfiguration } from '../../services/api/leads';
import { LEGACY_CZ_NUMBER_LOCALE, numberFormatter } from '../../utils/localeFormat';

/**
 * MKS-346 — pick the SLA pricing configurations priced for this lead.
 *
 * Search the existing ones by name or create one inline. Inline creation is a
 * real `POST /api/sla-pricing/configurations` followed by linking the returned
 * id — the same two-step `LeadContactsPicker` uses for an unknown contact, so
 * the lead write stays a pure link operation.
 *
 * Why inline rather than "open the configurator": the ticket asks for a
 * shortcut that opens the configurator and links the result back. Navigating
 * to `/sales/sla-pricing/:id` from here would discard every unsaved field on a
 * form with no draft persistence, so the configuration is created here (name +
 * currency, the two fields `NewConfigurationModal` asks for that matter before
 * a grid exists) and each linked row carries an "Open" link for painting the
 * 24x7 grid. The configurator opens in a new tab for the same reason.
 *
 * Currency is picked at creation and locks once the configuration has bands
 * (D-14, enforced server-side) — which is exactly why it is asked for here and
 * nowhere else in the lead form.
 */
const CURRENCIES = ['EUR', 'USD', 'CZK'] as const;

interface LeadSlaPricingPickerProps {
  value: LeadSlaPricingConfiguration[];
  onChange: (next: LeadSlaPricingConfiguration[]) => void;
}

/** A configuration, shaped as it appears on a lead. */
function toLeadConfiguration(
  configuration: SlaPricingConfigurationSummary,
): LeadSlaPricingConfiguration {
  return {
    id: configuration.id,
    name: configuration.name,
    currency: configuration.currency,
    monthlyPrice: configuration.monthlyPrice,
  };
}

/**
 * The monthly price as the configurator prints it: grouped thousands, the
 * currency beside it, and the backend's fixed-2 string parsed only for
 * formatting — never used for arithmetic.
 */
export function formatMonthlyPrice(monthlyPrice: string, currency: string): string {
  const amount = Number.parseFloat(monthlyPrice);
  if (!Number.isFinite(amount)) return `${monthlyPrice} ${currency}`;
  return `${numberFormatter({ maximumFractionDigits: 0 }, LEGACY_CZ_NUMBER_LOCALE).format(amount)} ${currency}/mo`;
}

export function LeadSlaPricingPicker({ value, onChange }: LeadSlaPricingPickerProps) {
  const [filter, setFilter] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [newCurrency, setNewCurrency] = useState<string>(CURRENCIES[0]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const createConfigurationMutation = useCreateConfiguration();
  // `GET /api/sla-pricing/configurations` is unpaginated by design (~30 rows),
  // so the search is a client-side name filter rather than a second endpoint.
  const { data: configurationsData } = useConfigurations();
  const allConfigurations = configurationsData?.data ?? [];
  const trimmedFilter = filter.trim();
  const available = allConfigurations.filter(
    (c) =>
      c.name.toLowerCase().includes(trimmedFilter.toLowerCase()) &&
      !value.some((picked) => picked.id === c.id),
  );

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const canCreate =
    trimmedFilter !== '' &&
    !available.some((c) => c.name.toLowerCase() === trimmedFilter.toLowerCase()) &&
    !value.some((c) => c.name.toLowerCase() === trimmedFilter.toLowerCase());

  const addConfiguration = (configuration: SlaPricingConfigurationSummary) => {
    onChange([...value, toLeadConfiguration(configuration)]);
    setFilter('');
    setIsOpen(false);
  };

  const removeConfiguration = (id: number) => {
    onChange(value.filter((c) => c.id !== id));
  };

  const createInline = async () => {
    if (!trimmedFilter) return;
    try {
      const created = await createConfigurationMutation.mutateAsync({
        name: trimmedFilter,
        currency: newCurrency,
      });
      addConfiguration(created.data);
    } catch {
      // The mutation's own error toast already reported it.
    }
  };

  return (
    <div className="col-span-2" ref={dropdownRef}>
      <label htmlFor="lead-sla-pricing" className="block text-sm font-medium text-gray-700 mb-1">
        SLA pricing
      </label>

      {value.length > 0 && (
        <ul className="mb-2 space-y-1">
          {value.map((linked) => {
            // Prefer the freshly fetched summary: someone who opens the
            // configurator in the other tab and paints the grid comes back to
            // the real price rather than the one the lead was loaded with.
            const configuration = allConfigurations.find((c) => c.id === linked.id) ?? linked;
            return (
            <li
              key={configuration.id}
              className="flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1.5"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-gray-900">{configuration.name}</p>
                <p className="truncate font-mono tabular-nums text-xs text-gray-500">
                  {formatMonthlyPrice(configuration.monthlyPrice, configuration.currency)}
                </p>
              </div>
              {/* A new tab, so an unsaved lead form survives the trip to the
                  configurator and back. */}
              <a
                href={`/sales/sla-pricing/${configuration.id}`}
                target="_blank"
                rel="noreferrer"
                aria-label={`Open ${configuration.name} in the configurator`}
                title="Open in the configurator"
                className="inline-flex shrink-0 items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
              >
                Open
                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
              </a>
              <button
                type="button"
                onClick={() => removeConfiguration(configuration.id)}
                aria-label={`Unlink ${configuration.name}`}
                className="shrink-0 p-0.5 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </li>
            );
          })}
        </ul>
      )}

      <div className="relative">
        <input
          id="lead-sla-pricing"
          type="text"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search configurations or type a new name..."
          className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle SLA pricing configurations list"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
        >
          <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-auto">
            {available.map((configuration) => (
              <button
                key={configuration.id}
                type="button"
                onClick={() => addConfiguration(configuration)}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50"
              >
                <span className="block truncate">{configuration.name}</span>
                <span className="block truncate font-mono tabular-nums text-xs text-gray-400">
                  {formatMonthlyPrice(configuration.monthlyPrice, configuration.currency)}
                </span>
              </button>
            ))}

            {canCreate && (
              <div className="border-t border-gray-100 p-2 space-y-2">
                <p className="text-xs text-gray-500">
                  New configuration &ldquo;{trimmedFilter}&rdquo; — pick its currency, then open it to
                  price the 24&times;7 grid.
                </p>
                <select
                  value={newCurrency}
                  onChange={(e) => setNewCurrency(e.target.value)}
                  aria-label="New configuration currency"
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {CURRENCIES.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={createInline}
                  disabled={createConfigurationMutation.isPending}
                  className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm text-green-700 hover:bg-green-50 disabled:opacity-50"
                >
                  <PlusIcon className="w-4 h-4" />
                  New configuration &ldquo;{trimmedFilter}&rdquo;
                </button>
              </div>
            )}

            {available.length === 0 && !canCreate && (
              <p className="px-3 py-2 text-sm text-gray-400">No configurations found</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default LeadSlaPricingPicker;
